import React, { useState } from "react";
import {
  Search,
  Plus,
  Edit2, // eslint-disable-line no-unused-vars
  Copy,
  CalendarDays,
  Users,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronDown,
  X,
  Check,
  Percent,
} from "lucide-react";

/* ─── Mock data ─── */
const initialCoupons = [
  {
    id: 1,
    title: "10% OFF the total booking.",
    desc: "First-time customers only.",
    code: "WELCOMED",
    dateRange: { start: "1 Mar 2026", end: "20 Mar 2026" },
    usageLimit: "One time use",
    active: true,
  },
  {
    id: 2,
    title: "10% OFF the total booking.",
    desc: "First-time customers only.",
    code: "WELCOMED",
    dateRange: { start: "1 Mar 2026", end: "20 Mar 2026" },
    usageLimit: "One time use",
    active: true,
  },
  {
    id: 3,
    title: "10% OFF the total booking.",
    desc: "First-time customers only.",
    code: "WELCOMED",
    dateRange: { start: "1 Mar 2026", end: "20 Mar 2026" },
    usageLimit: "One time use",
    active: true,
  },
  {
    id: 4,
    title: "10% OFF the total booking.",
    desc: "First-time customers only.",
    code: "WELCOMED",
    dateRange: { start: "1 Mar 2026", end: "20 Mar 2026" },
    usageLimit: "One time use",
    active: false,
  },
  {
    id: 5,
    title: "10% OFF the total booking.",
    desc: "First-time customers only.",
    code: "WELCOMED",
    dateRange: { start: "1 Mar 2026", end: "20 Mar 2026" },
    usageLimit: "One time use",
    active: false,
  },
];

const USAGE_OPTIONS = [
  "One time use",
  "Once in a month",
  "Two times in a month",
  "Unlimited time",
];

/* ─── Toggle ─── */
function Toggle({ on, onChange }) {
  return (
    <button
      onClick={() => onChange(!on)}
      style={{
        backgroundColor: "#e5e7eb",
        padding: "3px",
        width: 48,
        height: 26,
        borderRadius: 999,
        display: "flex",
        alignItems: "center",
        transition: "background-color 0.25s",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: "50%",
          backgroundColor: on ? "#FC8EAC" : "#ffffff",
          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
          transform: on ? "translateX(22px)" : "translateX(0px)",
          transition: "transform 0.25s, background-color 0.25s",
          display: "block",
          flexShrink: 0,
        }}
      />
    </button>
  );
}

/* ─── Usage Limit Dropdown ─── */
function UsageSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-600 focus:outline-none hover:border-gray-200 transition-all"
      >
        <span className={value ? "text-gray-700" : "text-gray-400"}>
          {value || "-select-"}
        </span>
        <ChevronDown size={14} className="text-gray-400" />
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-100 rounded-xl shadow-lg z-30 overflow-hidden">
          {USAGE_OPTIONS.map((opt) => (
            <button
              key={opt}
              onClick={() => {
                onChange(opt);
                setOpen(false);
              }}
              className={`w-full text-left px-4 py-3 text-[13px] font-manrope-regular hover:bg-gray-50 transition-all flex items-center justify-between ${value === opt ? "text-primary font-manrope-semibold" : "text-gray-600"}`}
            >
              {opt}
              {value === opt && (
                <Check size={13} className="text-primary" strokeWidth={2.2} />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Range Calendar ─── */
const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function sameDay(a, b) {
  return (
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function fmtShort(d) {
  return d ? `${d.getDate()} ${SHORT[d.getMonth()]} ${d.getFullYear()}` : "";
}

function MonthGrid({
  year,
  month,
  startDate,
  endDate,
  hovered,
  onHover,
  onSelect,
  today,
}) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = Array.from({ length: firstDay + daysInMonth }, (_, i) =>
    i < firstDay ? null : i - firstDay + 1,
  );

  const getDate = (d) => new Date(year, month, d);

  const isStart = (d) => sameDay(getDate(d), startDate);
  const isEnd = (d) => sameDay(getDate(d), endDate);
  const isToday = (d) => sameDay(getDate(d), today);
  const inRange = (d) => {
    const cur = getDate(d);
    const low =
      startDate && endDate
        ? startDate < endDate
          ? startDate
          : endDate
        : startDate;
    const high =
      startDate && endDate
        ? startDate < endDate
          ? endDate
          : startDate
        : hovered;
    if (!low || !high) return false;
    return cur > low && cur < high;
  };
  const isHovEnd = (d) =>
    startDate && !endDate && hovered && sameDay(getDate(d), hovered);

  return (
    <div>
      {/* Day labels */}
      <div className="grid grid-cols-7 mb-1">
        {DAYS.map((day, i) => (
          <div
            key={i}
            className="text-center text-[10px] font-manrope-semibold text-gray-400 py-1"
          >
            {day}
          </div>
        ))}
      </div>
      {/* Date cells */}
      <div className="grid grid-cols-7 gap-y-0.5">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const start = isStart(d);
          const end = isEnd(d);
          const range = inRange(d);
          const hovEnd = isHovEnd(d);
          const todayD = isToday(d);

          return (
            <div
              key={i}
              className="relative flex items-center justify-center h-8"
            >
              {/* Range highlight strip */}
              {(range ||
                (start && (endDate || hovered)) ||
                (end && startDate)) && (
                <div
                  className={`absolute inset-y-0 ${
                    start
                      ? "left-1/2 right-0"
                      : end
                        ? "left-0 right-1/2"
                        : "left-0 right-0"
                  } bg-primary/10`}
                />
              )}
              <button
                onClick={() => onSelect(getDate(d))}
                onMouseEnter={() => onHover(getDate(d))}
                onMouseLeave={() => onHover(null)}
                className={`relative z-10 w-8 h-8 rounded-full text-[12px] font-manrope-regular transition-all flex items-center justify-center
                  ${
                    start || end
                      ? "bg-primary text-white font-manrope-semibold"
                      : hovEnd
                        ? "bg-primary/30 text-primary"
                        : todayD
                          ? "border border-primary text-primary hover:bg-primary/10"
                          : "text-gray-700 hover:bg-gray-100"
                  }`}
              >
                {d}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RangeCalendar({ startDate, endDate, onChange, onClose }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const initLeft = startDate ? new Date(startDate) : today;
  const [leftView, setLeftView] = useState({
    year: initLeft.getFullYear(),
    month: initLeft.getMonth(),
  });
  const [hovered, setHovered] = useState(null);
  const [localStart, setLocalStart] = useState(startDate || null);
  const [localEnd, setLocalEnd] = useState(endDate || null);

  const rightYear = leftView.month === 11 ? leftView.year + 1 : leftView.year;
  const rightMonth = (leftView.month + 1) % 12;

  const prevLeft = () =>
    setLeftView((v) => {
      const d = new Date(v.year, v.month - 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  const nextLeft = () =>
    setLeftView((v) => {
      const d = new Date(v.year, v.month + 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });

  const handleSelect = (date) => {
    date.setHours(0, 0, 0, 0);
    if (!localStart || (localStart && localEnd)) {
      setLocalStart(date);
      setLocalEnd(null);
    } else {
      if (date < localStart) {
        setLocalEnd(localStart);
        setLocalStart(date);
      } else if (sameDay(date, localStart)) {
        setLocalStart(null);
      } else {
        setLocalEnd(date);
      }
    }
  };

  const handleApply = () => {
    onChange({ start: fmtShort(localStart), end: fmtShort(localEnd) });
    onClose();
  };

  const handleClear = () => {
    setLocalStart(null);
    setLocalEnd(null);
  };

  const headerLabel = () => {
    if (!localStart) return "Select start date";
    if (!localEnd) return `${fmtShort(localStart)} → ...`;
    return `${fmtShort(localStart)} → ${fmtShort(localEnd)}`;
  };

  return (
    <div className="bg-white rounded-2xl shadow-2xl overflow-hidden z-50 w-[560px]">
      {/* Pink header */}
      <div className="bg-primary px-5 py-4 flex items-center justify-between">
        <div>
          <p className="text-[10px] font-manrope-regular text-white/70 mb-0.5">
            Expiry date range
          </p>
          <p className="text-[14px] font-manrope-bold text-white">
            {headerLabel()}
          </p>
        </div>
        <button
          onClick={onClose}
          className="w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-all"
        >
          <X size={13} className="text-white" />
        </button>
      </div>

      {/* Month navigation + grids */}
      <div className="flex gap-4 px-5 pt-4 pb-3">
        {/* Left month */}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={prevLeft}
              className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center transition-all"
            >
              <ChevronLeft size={14} className="text-gray-500" />
            </button>
            <span className="text-[13px] font-manrope-semibold text-gray-800">
              {MONTHS[leftView.month]} {leftView.year}
            </span>
            <div className="w-7" />
          </div>
          <MonthGrid
            year={leftView.year}
            month={leftView.month}
            startDate={localStart}
            endDate={localEnd}
            hovered={hovered}
            onHover={setHovered}
            onSelect={handleSelect}
            today={today}
          />
        </div>

        {/* Divider */}
        <div className="w-px bg-gray-100 self-stretch mx-1" />

        {/* Right month */}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-3">
            <div className="w-7" />
            <span className="text-[13px] font-manrope-semibold text-gray-800">
              {MONTHS[rightMonth]} {rightYear}
            </span>
            <button
              onClick={nextLeft}
              className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center transition-all"
            >
              <ChevronRight size={14} className="text-gray-500" />
            </button>
          </div>
          <MonthGrid
            year={rightYear}
            month={rightMonth}
            startDate={localStart}
            endDate={localEnd}
            hovered={hovered}
            onHover={setHovered}
            onSelect={handleSelect}
            today={today}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
        <button
          onClick={handleClear}
          className="text-[12px] font-manrope-semibold text-gray-400 hover:text-gray-600 transition-all"
        >
          Clear
        </button>
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-gray-200 text-[12px] font-manrope-semibold text-gray-600 hover:bg-gray-50 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={!localStart}
            className="px-4 py-2 rounded-xl bg-primary text-white text-[12px] font-manrope-semibold hover:opacity-90 disabled:opacity-40 transition-all"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Date range input trigger ─── */
function DateRangeInput({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const display = value?.start
    ? value.end
      ? `${value.start}  →  ${value.end}`
      : `${value.start}  →  ...`
    : null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular hover:border-gray-200 transition-all"
      >
        <span
          className={
            display ? "text-gray-700 font-manrope-medium" : "text-gray-400"
          }
        >
          {display || "-select date range-"}
        </span>
        <CalendarDays size={14} className="text-gray-400 flex-shrink-0" />
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/20 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <div className="relative">
            <RangeCalendar
              startDate={value?.startRaw}
              endDate={value?.endRaw}
              onChange={(range) => onChange(range)}
              onClose={() => setOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Add / Edit Coupon Modal ─── */
function CouponModal({ mode = "add", coupon = null, onClose, onSave }) {
  const isEdit = mode === "edit";
  const [title, setTitle] = useState(isEdit ? coupon.title : "");
  const [desc, setDesc] = useState(isEdit ? coupon.desc : "");
  const [code, setCode] = useState(isEdit ? coupon.code : "");
  const [dateRange, setDateRange] = useState(isEdit ? coupon.dateRange : null);
  const [usageLimit, setUsageLimit] = useState(isEdit ? coupon.usageLimit : "");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl w-[400px] p-6 flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-[16px] font-manrope-bold text-gray-900">
            {isEdit ? "Edit coupon details" : "Add new coupon"}
          </h2>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center transition-all"
          >
            <X size={15} className="text-gray-500" />
          </button>
        </div>

        {/* Title */}
        <div>
          <label className="block text-[13px] font-manrope-semibold text-gray-700 mb-1.5">
            Title
          </label>
          <input
            type="text"
            placeholder="Enter coupon title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all"
          />
        </div>

        {/* Usage description */}
        <div>
          <label className="block text-[13px] font-manrope-semibold text-gray-700 mb-1.5">
            Usage description
          </label>
          <textarea
            placeholder="Enter usage description"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            rows={3}
            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all resize-none"
          />
        </div>

        {/* Code */}
        <div>
          <label className="block text-[13px] font-manrope-semibold text-gray-700 mb-1.5">
            Code
          </label>
          <input
            type="text"
            placeholder="Enter the coupon code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-semibold text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all tracking-wider"
          />
        </div>

        {/* Expiry date range */}
        <div>
          <label className="block text-[13px] font-manrope-semibold text-gray-700 mb-1.5">
            Expiry date range
          </label>
          <DateRangeInput value={dateRange} onChange={setDateRange} />
        </div>

        {/* Usage limit */}
        <div>
          <label className="block text-[13px] font-manrope-semibold text-gray-700 mb-1.5">
            Usage limit
          </label>
          <UsageSelect value={usageLimit} onChange={setUsageLimit} />
        </div>

        {/* Submit */}
        <button
          onClick={() => {
            onSave({ title, desc, code, dateRange, usageLimit });
            onClose();
          }}
          className="w-full py-3 bg-primary rounded-xl text-white text-[14px] font-manrope-semibold hover:opacity-90 transition-all mt-1"
        >
          {isEdit ? "Save changes" : "Add"}
        </button>
      </div>
    </div>
  );
}

/* ─── Coupon Row ─── */
function CouponRow({ coupon, onEdit, onToggle, onCopy }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(coupon.code).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    onCopy && onCopy();
  };

  return (
    <div className="bg-white rounded-2xl px-5 py-4 flex items-center gap-4">
      {/* Icon */}
      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
        <Percent size={16} className="text-gray-400" strokeWidth={1.8} />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-manrope-bold text-gray-900 truncate">
          {coupon.title}
        </p>
        <p className="text-[11px] font-manrope-regular text-gray-400 mb-2">
          {coupon.desc}
        </p>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1 text-[11px] font-manrope-regular text-gray-400">
            <CalendarDays size={11} strokeWidth={1.6} />
            {coupon.dateRange?.start && coupon.dateRange?.end ? (
              <span>
                {coupon.dateRange.start} → {coupon.dateRange.end}
              </span>
            ) : coupon.dateRange?.start ? (
              <span>From {coupon.dateRange.start}</span>
            ) : (
              <span>No date set</span>
            )}
          </div>
          <div className="flex items-center gap-1 text-[11px] font-manrope-regular text-gray-400">
            <Users size={11} strokeWidth={1.6} />
            <span>{coupon.usageLimit}</span>
          </div>
        </div>
      </div>

      {/* Code badge */}
      <div className="flex items-center gap-1.5 bg-primary/10 border border-primary/20 rounded-lg px-3 py-1.5 flex-shrink-0">
        <span className="text-[12px] font-manrope-bold text-primary tracking-wider">
          {coupon.code}
        </span>
        <button
          onClick={handleCopy}
          className="hover:opacity-70 transition-all"
        >
          {copied ? (
            <Check size={12} className="text-primary" strokeWidth={2.5} />
          ) : (
            <Copy size={12} className="text-primary" strokeWidth={1.8} />
          )}
        </button>
      </div>

      {/* Edit */}
      <button
        onClick={onEdit}
        className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-all flex-shrink-0"
      >
        <Edit2 size={13} className="text-gray-500" strokeWidth={1.6} />
      </button>

      {/* Toggle */}
      <Toggle on={coupon.active} onChange={onToggle} />
    </div>
  );
}

/* ─── Main Page ─── */
export default function Coupons() {
  const [coupons, setCoupons] = useState(initialCoupons);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null); // "add" | "edit"
  const [editTarget, setEditTarget] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = 5;

  const filtered = coupons.filter(
    (c) =>
      c.title.toLowerCase().includes(search.toLowerCase()) ||
      c.code.toLowerCase().includes(search.toLowerCase()),
  );

  const handleAdd = (data) => {
    setCoupons((prev) => [...prev, { id: Date.now(), ...data, active: true }]);
  };

  const handleEdit = (data) => {
    setCoupons((prev) =>
      prev.map((c) => (c.id === editTarget.id ? { ...c, ...data } : c)),
    );
  };

  const handleToggle = (id, val) => {
    setCoupons((prev) =>
      prev.map((c) => (c.id === id ? { ...c, active: val } : c)),
    );
  };

  const pageNumbers = [1, 2, "...", totalPages];

  return (
    <>
      <div className="flex flex-col min-h-screen">
        {/* Header */}
        <header className="flex items-center justify-between px-8 pt-7 pb-4">
          <div>
            <h1 className="text-[22px] font-manrope-bold text-gray-900">
              Coupons management
            </h1>
            <p className="text-[12px] font-manrope-regular text-gray-400 mt-0.5">
              You can manage all of your coupons from here.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-9 h-9 rounded-full bg-white border border-gray-100 flex items-center justify-center shadow-sm">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                className="text-gray-600"
              >
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center">
                <span className="text-sm font-manrope-bold text-gray-600">
                  V
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-[13px] font-manrope-semibold text-gray-900 leading-tight">
                  Vigelanto
                </span>
                <span className="text-[11px] font-manrope-regular text-gray-400 leading-tight">
                  muhammadmridulhosenkibria@gmail.com
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 px-8 pb-8">
          {/* Toolbar */}
          <div className="flex items-center gap-3 mb-5">
            <div className="relative flex-1 max-w-xs">
              <input
                type="text"
                placeholder="Search coupon"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-4 pr-4 py-2.5 bg-white border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all"
              />
            </div>
            <button className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center hover:opacity-90 transition-all flex-shrink-0">
              <Search size={16} className="text-white" strokeWidth={2} />
            </button>
            <button
              onClick={() => setModal("add")}
              className="flex items-center gap-2 px-4 py-2.5 bg-primary rounded-xl text-white text-[13px] font-manrope-semibold hover:opacity-90 transition-all ml-auto"
            >
              <Plus size={15} strokeWidth={2.2} />
              Add new coupon code
            </button>
          </div>

          {/* Coupon list */}
          <div className="flex flex-col gap-3">
            {filtered.length === 0 ? (
              <div className="bg-white rounded-2xl py-16 flex flex-col items-center gap-2">
                <p className="text-[13px] font-manrope-semibold text-gray-400">
                  No coupons found
                </p>
              </div>
            ) : (
              filtered.map((coupon) => (
                <CouponRow
                  key={coupon.id}
                  coupon={coupon}
                  onEdit={() => {
                    setEditTarget(coupon);
                    setModal("edit");
                  }}
                  onToggle={(val) => handleToggle(coupon.id, val)}
                />
              ))
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between mt-5">
            <span className="text-[12px] font-manrope-regular text-gray-500">
              Total{" "}
              <span className="font-manrope-bold text-gray-800">
                {filtered.length}
              </span>{" "}
              coupons
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:bg-white disabled:opacity-30 transition-all"
              >
                <ChevronsLeft size={14} />
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:bg-white disabled:opacity-30 transition-all"
              >
                <ChevronLeft size={14} />
              </button>
              {pageNumbers.map((p, i) =>
                p === "..." ? (
                  <span
                    key={`el-${i}`}
                    className="w-7 h-7 flex items-center justify-center text-[12px] font-manrope-regular text-gray-400"
                  >
                    ...
                  </span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`w-7 h-7 rounded-md flex items-center justify-center text-[12px] font-manrope-semibold transition-all ${currentPage === p ? "bg-primary text-white" : "text-gray-500 hover:bg-white"}`}
                  >
                    {p}
                  </button>
                ),
              )}
              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
                className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:bg-white disabled:opacity-30 transition-all"
              >
                <ChevronRight size={14} />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:bg-white disabled:opacity-30 transition-all"
              >
                <ChevronsRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add modal */}
      {modal === "add" && (
        <CouponModal
          mode="add"
          onClose={() => setModal(null)}
          onSave={handleAdd}
        />
      )}

      {/* Edit modal */}
      {modal === "edit" && editTarget && (
        <CouponModal
          mode="edit"
          coupon={editTarget}
          onClose={() => {
            setModal(null);
            setEditTarget(null);
          }}
          onSave={handleEdit}
        />
      )}
    </>
  );
}
